/**
 * Tileable value noise for the procedural surface textures.
 *
 * The materials used to sample `sin(x * 12.9898 + y * 78.233) * 43758.5453`
 * directly per pixel. That is a hash, not noise: neighbouring pixels are
 * uncorrelated, so it produces white-noise sparkle with a visible diagonal
 * bias rather than the smooth clumping of leather grain or foam pores. It also
 * does not tile, so the seams of a repeated texture never quite line up.
 *
 * Here the hash is evaluated on an integer lattice and interpolated smoothly
 * between lattice points, and the lattice wraps at a period — so every octave
 * tiles, and the texture can therefore be repeated across a panel without
 * seams.
 */

/** Deterministic hash of a lattice point to [0, 1). */
function hash(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

/** Smoothstep, so the interpolated field has no lattice-aligned creases. */
function fade(t: number): number {
  return t * t * (3 - 2 * t);
}

const wrap = (value: number, period: number): number =>
  ((value % period) + period) % period;

/**
 * Value noise at (x, y), wrapping every `periodX` / `periodY` units.
 *
 * Separate periods per axis are what allow a stretched pattern — wood grain
 * runs along the board, so it needs many lattice cells across the fibres and
 * very few along them — while still tiling exactly in both directions.
 */
export function tileableValueNoise2(
  x: number,
  y: number,
  periodX: number,
  periodY: number,
): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = fade(x - x0);
  const fy = fade(y - y0);

  const x0w = wrap(x0, periodX);
  const y0w = wrap(y0, periodY);
  const x1w = wrap(x0 + 1, periodX);
  const y1w = wrap(y0 + 1, periodY);

  const c00 = hash(x0w, y0w);
  const c10 = hash(x1w, y0w);
  const c01 = hash(x0w, y1w);
  const c11 = hash(x1w, y1w);

  const top = c00 + (c10 - c00) * fx;
  const bottom = c01 + (c11 - c01) * fx;
  return top + (bottom - top) * fy;
}

/**
 * Value noise at (x, y), wrapping every `period` units in both axes.
 *
 * @param period Lattice period; must be a positive integer for the result to
 *   tile exactly.
 */
export const tileableValueNoise = (
  x: number,
  y: number,
  period: number,
): number => tileableValueNoise2(x, y, period, period);

/**
 * Fractal sum of {@link tileableValueNoise}.
 *
 * Each octave doubles both the frequency and the lattice period, so the whole
 * sum keeps tiling. Returns roughly [0, 1].
 *
 * @param u Tile coordinate in [0, 1).
 * @param v Tile coordinate in [0, 1).
 * @param period Lattice cells across the tile at the first octave.
 */
export function tileableFbm(
  u: number,
  v: number,
  period: number,
  octaves: number,
): number {
  return tileableFbm2(u, v, period, period, octaves);
}

/**
 * Fractal sum of {@link tileableValueNoise2}, with a period per axis.
 *
 * @param periodU Lattice cells along the tile's U axis at the first octave.
 * @param periodV Lattice cells along its V axis.
 */
export function tileableFbm2(
  u: number,
  v: number,
  periodU: number,
  periodV: number,
  octaves: number,
): number {
  let value = 0;
  let amplitude = 0.5;
  let total = 0;
  let frequencyU = Math.max(1, Math.round(periodU));
  let frequencyV = Math.max(1, Math.round(periodV));

  for (let octave = 0; octave < octaves; octave++) {
    value +=
      amplitude *
      tileableValueNoise2(
        u * frequencyU,
        v * frequencyV,
        frequencyU,
        frequencyV,
      );
    total += amplitude;
    amplitude *= 0.5;
    frequencyU *= 2;
    frequencyV *= 2;
  }

  return total > 0 ? value / total : 0;
}

/**
 * Jittered feature points on a wrapping grid, for {@link cellularEdgeDistance}.
 *
 * Precomputed rather than hashed per lookup, and hashed with integer mixing
 * rather than the `sin` above. A cellular field costs nine distance tests per
 * texel, so hashing inside that loop would hash every point nine times over,
 * and `sin` is the single most expensive thing in this file. Together the two
 * are worth about 16x: a 512x512 cellular bake takes ~15 ms this way against
 * ~240 ms the obvious way, which is what makes a foam texture fine enough to
 * show its pores affordable at first paint.
 *
 * {@link tileableValueNoise} keeps the `sin` hash: it reads four corners, not
 * nine points, and changing it would restyle every leather, wood and aluminium
 * texture in the product.
 */
export type CellularLattice = Readonly<{
  cells: number;
  x: Float32Array;
  y: Float32Array;
}>;

/** Integer hash to [0, 1). Two multiply-xor-shift rounds, no transcendentals. */
function integerHash(x: number, y: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  // Both operands below coerce h to 32 bits, including an overflowing sum.
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * How far a feature point may stray from its cell centre, as a fraction of the
 * cell.
 *
 * Not the full cell: unrestricted jitter lets two points land almost on top of
 * each other, which in a foam reads as one huge pore next to a cluster of
 * slivers. Holding a margin at the cell walls is a cheap stand-in for the
 * Poisson-disc spacing real foam has, where the cells nucleate at once and stop
 * growing where they meet.
 */
const CELL_JITTER = 0.76;

/**
 * Feature points for a `cells` x `cells` grid wrapping in both axes.
 *
 * @param salt Distinguishes lattices that must not line up — stacked layers of
 *   the same foam, above all.
 */
export function cellularLattice(cells: number, salt: number): CellularLattice {
  const count = cells * cells;
  const x = new Float32Array(count);
  const y = new Float32Array(count);
  const margin = (1 - CELL_JITTER) / 2;

  for (let cy = 0; cy < cells; cy++) {
    for (let cx = 0; cx < cells; cx++) {
      const index = cy * cells + cx;
      x[index] = cx + margin + CELL_JITTER * integerHash(cx + salt, cy + salt);
      y[index] =
        cy +
        margin +
        CELL_JITTER * integerHash(cx + salt + 7919, cy + salt - 104729);
    }
  }

  return { cells, x, y };
}

/**
 * Distance from the second-nearest feature point to the nearest, in cell units.
 *
 * Zero exactly on a Voronoi boundary and largest at a cell centre, so
 * thresholding it draws the boundary network at a controllable thickness. That
 * network is the point: an open-cell foam is a graph of ligaments meeting at
 * vertices with open voids between them, and a Voronoi tessellation is the
 * standard way to generate that topology. Fractal value noise has no such
 * structure at any amplitude — it clumps, which reads as stucco.
 *
 * @param u Tile coordinate in [0, 1).
 * @param v Tile coordinate in [0, 1).
 */
export function cellularEdgeDistance(
  lattice: CellularLattice,
  u: number,
  v: number,
): number {
  return cellularSearch(lattice, u, v);
}

/** What {@link cellularNearest} finds at a point. */
export type CellularSample = Readonly<{
  /** {@link cellularEdgeDistance} at the point. */
  edge: number;
  /**
   * Index of the nearest feature point in the lattice, in `[0, cells²)`.
   *
   * Stable across the whole cell, so anything hashed from it — a depth, a
   * tint — is constant per cell and steps at the cell wall. That is what a
   * cut through closed-cell foam looks like: each cell is its own bubble,
   * sliced at its own height.
   */
  index: number;
}>;

/**
 * Nearest feature point to (u, v) and the edge distance there, in one search.
 */
export function cellularNearest(
  lattice: CellularLattice,
  u: number,
  v: number,
): CellularSample {
  return { edge: cellularSearch(lattice, u, v), index: nearestFeatureIndex };
}

/**
 * Index of the feature point the last {@link cellularSearch} settled on.
 *
 * A module-level out-parameter rather than a second return value: the search
 * is the inner loop of the foam bake — a quarter of a million texels times
 * three layers — and only {@link cellularNearest} wants the index, so paying
 * an object allocation per call to carry it would tax the caller that throws
 * it away. Read it immediately after the call; nothing else writes it.
 */
let nearestFeatureIndex = 0;

/**
 * The edge distance at (u, v), leaving the winning index in
 * {@link nearestFeatureIndex}.
 */
function cellularSearch(
  lattice: CellularLattice,
  u: number,
  v: number,
): number {
  const { cells, x, y } = lattice;
  const px = u * cells;
  const py = v * cells;
  const cx = Math.floor(px);
  const cy = Math.floor(py);

  // Squared distances throughout the search; only the two winners are rooted.
  let nearest = Number.POSITIVE_INFINITY;
  let second = Number.POSITIVE_INFINITY;
  let nearestIndex = 0;

  for (let j = -1; j <= 1; j++) {
    // The neighbour's index wraps, but its position must not: a point one cell
    // off the left edge has to be compared where it lies, not where its index
    // points, or every tile border grows a seam of coincident points.
    const row = cy + j;
    const wrappedRow = ((row % cells) + cells) % cells;
    const rowOffset = row - wrappedRow;

    for (let i = -1; i <= 1; i++) {
      const column = cx + i;
      const wrappedColumn = ((column % cells) + cells) % cells;
      const columnOffset = column - wrappedColumn;

      const index = wrappedRow * cells + wrappedColumn;
      const dx = (x[index] ?? 0) + columnOffset - px;
      const dy = (y[index] ?? 0) + rowOffset - py;
      const distance = dx * dx + dy * dy;

      if (distance < nearest) {
        second = nearest;
        nearest = distance;
        nearestIndex = index;
      } else if (distance < second) {
        second = distance;
      }
    }
  }

  nearestFeatureIndex = nearestIndex;
  return Math.sqrt(second) - Math.sqrt(nearest);
}

/** Deterministic value in [0, 1) for a cell of a lattice; see {@link CellularSample.index}. */
export function cellularHash(index: number, salt: number): number {
  return integerHash(index + salt, salt * 31 + 7);
}
